/**
 * 浏览器下载 helper —— 通过临时 <a download> 触发,避开 popup blocker。
 *
 * 后端给的 presigned URL 自带 `Content-Disposition: attachment` 查询参数,
 * 浏览器跟随后会直接下载。但 `window.open(url, '_blank')` 在 dev 跨 origin
 * (5173 → 9000) 经常被 popup blocker 拦,且部分场景空 tab 不会自动关。
 * 用 anchor.click() 是最稳的原生下载触发方式。
 */
export function triggerDownload(url: string, filename?: string): void {
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  // download 属性在跨 origin 时会被忽略, presigned URL 已经带了 disposition,所以无所谓
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * 下载文件名 = 安全化作品名 + 后缀(1004 F23 #9;与 api 海报下载 `PosterPayloadMapper.sanitizeFilename` 同规则):
 * 路径分隔 / 保留符 / 控制符 → 下划线,折叠空白;作品名空白 → 用 `fallback`。
 * ★跨域签名链接上 `<a download>` 会被浏览器忽略,真正生效的是后端签进 response-content-disposition 的名字 ——
 *   这里只保证同域 / 本地预览时两处视频下载口与海报同一个名字规则,根治要后端签名时也用作品名。
 */
export function mediaDownloadFilename(
  name: string | null | undefined,
  fallback: string,
  ext: string
): string {
  // eslint-disable-next-line no-control-regex
  const cleaned = (name ?? "").trim().replace(/[\\/:*?"<>|\u0000-\u001f\u007f]/g, "_").replace(/\s+/g, " ").trim();
  return `${cleaned || fallback}.${ext.replace(/^\./, "")}`;
}

/**
 * 图片下载扩展名按对象真实后缀取(1005 生产走查:长图成品是 JPEG,下载名却写死 `.png`)。
 * 签名链接的路径就是对象 key(`…/seq1_final.jpg`),key 后缀由落盘 mime 决定;query 里的
 * disposition 文件名不可信(后端旧口径写死 .png)。取不到 / 非图片后缀 → `png`(与旧行为一致)。
 */
export function imageExtFromUrl(url: string): "png" | "jpg" | "webp" {
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch {
    path = url.split("?")[0] ?? "";
  }
  const m = /\.([a-z0-9]+)$/i.exec(path);
  const ext = (m?.[1] ?? "").toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return "jpg";
  if (ext === "webp") return "webp";
  return "png";
}
